import { Component, signal, computed, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { rxResource } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { MatSnackBar } from '@angular/material/snack-bar';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { EventsService, UserEvent } from '../../services/events.service';
import { EventCardComponent } from '../../common-components/event-card/event-card.component';
import { ModalComponent, ModalButton } from '../../common-components/modal/modal.component';

@Component({
  selector: 'app-events',
  imports: [CommonModule, TranslocoModule, ReactiveFormsModule, EventCardComponent, ModalComponent],
  templateUrl: './events.component.html',
  styleUrl: './events.component.scss'
})
export class EventsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  currentTab = signal<string>('participant');
  searchQuery = signal<string>('');
  searchControl = new FormControl<string>('', { nonNullable: true });  eventsActive = rxResource({
    stream: () => this.eventsService.getEvents$({ tabName: 'active' })
  });

  eventsParticipant = rxResource({
    stream: () => this.eventsService.getEvents$({ tabName: 'participant' })
  });

  eventsMine = rxResource({
    stream: () => this.eventsService.getEvents$({ tabName: 'mine' })
  });

  events = computed(() => {
    const tab = this.currentTab();

    switch (tab) {
      case 'active':
        return this.eventsActive.value() || [];
      case 'participant':
        return this.eventsParticipant.value() || [];
      case 'mine':
        return this.eventsMine.value() || [];
      default:
        return [];
    }
  });

  filteredEvents = computed(() => {
    const query = this.searchQuery().toLowerCase();
    if (!query) return this.events();

    return this.events().filter(event =>
      event.name?.toLowerCase().includes(query) ||
      event.description?.toLowerCase().includes(query)
    );
  });

  isLoading = computed(() => {
    const tab = this.currentTab();
    switch (tab) {
      case 'active':
        return this.eventsActive.isLoading();
      case 'participant':
        return this.eventsParticipant.isLoading();
      case 'mine':
        return this.eventsMine.isLoading();
      default:
        return false;
    }
  });

  showDeleteModal = signal(false);
  selectedEvent = signal<UserEvent | null>(null);

  deleteModalButtons: ModalButton[] = [
    {
      type: 'secondary',
      text: this.transloco.translate('Cancel'),
      onClick: () => this.showDeleteModal.set(false)
    },
    {
      type: 'error',
      text: this.transloco.translate('Delete'),
      onClick: () => this.confirmDelete()
    }
  ];

  constructor(
    private eventsService: EventsService,
    private router: Router,
    private transloco: TranslocoService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    // Subscribe to search control value changes with debounce
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        this.searchQuery.set(value);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectTab(tab: string): void {
    this.currentTab.set(tab);
  }


  createEvent(): void {
    this.router.navigate(['/edit-event', 'new']);
  }

  viewEvent(event: UserEvent): void {
    this.router.navigate(['/event', event.id]);
  }

  editEvent(event: UserEvent): void {
    this.router.navigate(['/edit-event', event.id]);
  }

  openDeleteModal(event: UserEvent, $event?: Event): void {
    $event?.stopPropagation();
    this.selectedEvent.set(event);
    this.showDeleteModal.set(true);
  }

  confirmDelete(): void {
    const event = this.selectedEvent();
    if (!event) return;

    this.eventsService.deleteEvent$(event).subscribe({
      next: () => {
        this.snackBar.open(
          this.transloco.translate('eventDeletedSuccessfully'),
          'OK',
          { duration: 3000 }
        );
        this.showDeleteModal.set(false);
        this.reloadEvents();
      },
      error: (error) => {
        console.error('Error deleting event:', error);
        this.snackBar.open(
          this.transloco.translate('failedToDeleteEvent'),
          'OK',
          { duration: 3000 }
        );
      }
    });
  }

  reloadEvents(): void {
    this.eventsActive.reload();
    this.eventsParticipant.reload();
    this.eventsMine.reload();
  }
}
